import React, { useState } from 'react';
import { useNavigate } from '../../../hooks/useNavigate';
import {
  Box,
  IconButton,
  InputBase,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { RADIUS } from '../../../theme/tokens';

const MIN_TERM_LENGTH = 3;

interface SearchFieldProps {
  autoFocus?: boolean;
  onSearched?: () => void;
}

function SearchField({ autoFocus, onSearched }: SearchFieldProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim().length >= MIN_TERM_LENGTH) {
      navigate(`/busca?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
      onSearched?.();
    }
  };

  return (
    <Box
      component="form"
      role="search"
      onSubmit={handleSearch}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 48,
        pl: 2,
        pr: 0.5,
        borderRadius: `${RADIUS.control}px`,
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.subtle',
        transition: 'border-color 200ms, background-color 200ms, box-shadow 200ms',
        '&:hover': {
          borderColor: 'grey.400',
        },
        '&:focus-within': {
          borderColor: 'brand.text',
          backgroundColor: 'background.paper',
          boxShadow: (theme) => `0 0 0 3px ${theme.palette.brand.surfaceStrong}`,
        },
      }}
    >
      <InputBase
        placeholder="Buscar no portal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        inputProps={{ 'aria-label': 'Buscar no portal', minLength: MIN_TERM_LENGTH }}
        sx={{ flex: 1, fontSize: '1rem' }}
        autoFocus={autoFocus}
      />
      <IconButton type="submit" aria-label="pesquisar" sx={{ color: 'brand.text', width: 44, height: 44 }}>
        <SearchIcon />
      </IconButton>
    </Box>
  );
}

/** Campo de busca sempre visível (cabeçalho no desktop). */
export default function SearchBar() {
  return <SearchField />;
}

/** Botão que abre a busca em linha abaixo do cabeçalho (mobile). */
export function SearchToggleButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <IconButton
      onClick={onToggle}
      aria-label={open ? 'fechar busca' : 'abrir busca'}
      aria-expanded={open}
      aria-controls="busca-mobile"
      sx={{ width: 44, height: 44, color: 'text.primary' }}
    >
      {open ? <CloseIcon /> : <SearchIcon />}
    </IconButton>
  );
}

export function SearchPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Collapse in={open} unmountOnExit id="busca-mobile">
      <Box sx={{ pb: 2 }}>
        <SearchField autoFocus onSearched={onClose} />
      </Box>
    </Collapse>
  );
}
